import * as tf from "@tensorflow/tfjs"
import { Database, PivotSuggestion } from "../typings"

const generateViable1m = async (
  selectedTags: string[],
  model: tf.LayersModel,
  database: Database
) => {
  const oneHotTags = selectedTags.map(tag =>
    tf.oneHot(database.tags.indexOf(tag), database.tags.length)
  )
  const inputData = tf.addN(oneHotTags).reshape([1, 382])
  const predictionPromise = model.predict(inputData) as tf.Tensor<tf.Rank>
  const predictionResult = (await predictionPromise.array())[0][0]

  const viable1m = predictionResult >= 0.5
  return viable1m
}

const generatePivotTags = (selectedTags: string[], database: Database) => {
  const [tag1, tag2, tag3] = selectedTags

  const generateTags = (tag1: string, tag2: string, database: Database) => {
    return database.tags.map(tag => [tag1, tag2, tag])
  }

  return [
    ...generateTags(tag1, tag2, database),
    ...generateTags(tag1, tag3, database),
    ...generateTags(tag2, tag3, database),
  ]
}

const generatePivotSuggestions = async (
  selectedTags: string[],
  model: tf.LayersModel,
  database: Database
) => {
  const inputDataAsStrings = generatePivotTags(selectedTags, database)
  const inputDataAsJavascriptArrays = inputDataAsStrings.map(tags => {
    const row = new Array<number>(database.tags.length).fill(0.0)
    for (var tag of tags) {
      const index = database.tags.indexOf(tag)
      row[index] = 1.0
    }
    return row
  })
  const inputDataAsTensors = tf.tensor2d(inputDataAsJavascriptArrays, [
    inputDataAsJavascriptArrays.length,
    database.tags.length,
  ])
  const predictionPromise = model.predict(inputDataAsTensors) as tf.Tensor<
    tf.Rank
  >
  const predictions = (await predictionPromise.array()) as number[][]

  let result: PivotSuggestion[] = []
  predictions.forEach(([prediction], index) => {
    if (prediction > 0.5) {
      result.push({
        tags: inputDataAsStrings[index],
        viable1m: true,
      })
    }
  })
  return result
}

const generateRelevantGames = (selectedTags: string[], database: Database) => {
  return database.games.filter(game => {
    const intersectionTags = game.tags.filter(tag => selectedTags.includes(tag))
    return intersectionTags.length >= 2
  })
}

const generateReportData = async (
  selectedTags: string[],
  model: tf.LayersModel,
  database: Database
) => ({
  tags: selectedTags,
  viable1m: await generateViable1m(selectedTags, model, database),
  pivotSuggestions: await generatePivotSuggestions(
    selectedTags,
    model,
    database
  ),
  relevantGames: generateRelevantGames(selectedTags, database),
})

export default generateReportData
